import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { usps, services as fallbackServices } from '../data';
import { useCms } from '../context/CmsContext';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import ReviewsSlider from '../components/ReviewsSlider';
import DenmarkMap from '../components/DenmarkMap';

// Helper to determine Font Awesome icon safely from service type
const getServiceIcon = (titleOrSlug, idx = 0) => {
  const str = String(titleOrSlug || '').toLowerCase();
  if (str.includes('fredede') || str.includes('kulturarv') || str.includes('historisk')) return 'fa-solid fa-landmark';
  if (str.includes('facade') || str.includes('mur') || str.includes('tegl')) return 'fa-solid fa-trowel-bricks';
  if (str.includes('graffiti') || str.includes('beton') || str.includes('natursten')) return 'fa-solid fa-spray-can';
  if (str.includes('rust') || str.includes('metal')) return 'fa-solid fa-screwdriver-wrench';
  if (str.includes('industri') || str.includes('skade')) return 'fa-solid fa-industry';
  const icons = ['fa-solid fa-landmark', 'fa-solid fa-trowel-bricks', 'fa-solid fa-spray-can', 'fa-solid fa-screwdriver-wrench', 'fa-solid fa-industry'];
  return icons[idx % icons.length];
};

// Helper to get matching standard route slug
const getServiceRouteSlug = (service, idx = 0) => {
  const sTitle = typeof service?.title === 'object' ? service.title?.da : service?.title || '';
  const sSlug = typeof service?.slug === 'object' ? service.slug?.da : service?.slug || '';
  const str = (sSlug + ' ' + sTitle).toLowerCase();
  if (str.includes('fredede')) return 'fredede-bygninger';
  if (str.includes('facade') || str.includes('mur')) return 'facaderens-murvaerk';
  if (str.includes('graffiti') || str.includes('beton')) return 'graffiti-beton-natursten';
  if (str.includes('rust') || str.includes('metal')) return 'rust-metaloverflader';
  if (str.includes('industri') || str.includes('skade')) return 'industri-skadeservice';
  const defaultSlugs = ['fredede-bygninger', 'facaderens-murvaerk', 'graffiti-beton-natursten', 'rust-metaloverflader', 'industri-skadeservice'];
  return defaultSlugs[idx % defaultSlugs.length];
};

export default function Home() {
  const { services, cases, getPage } = useCms();
  const [showAllUsps, setShowAllUsps] = useState(false);
  const pageData = getPage('index');
  const displayCases = cases ? cases.slice(0, 3) : [];
  const displayServices = services && services.length > 0 ? services : fallbackServices;

  // Extract parsed sections from CMS content_html if available
  const parsedDoc = React.useMemo(() => {
    const html = typeof pageData?.content_html === 'object' ? pageData.content_html?.da : pageData?.content_html;
    if (!html || typeof window === 'undefined') return null;
    try {
      const parser = new DOMParser();
      return parser.parseFromString(html, 'text/html');
    } catch {
      return null;
    }
  }, [pageData?.content_html]);

  // Dynamic content extractions with exact fallbacks
  const heroContent = React.useMemo(() => {
    if (!parsedDoc) return null;
    const h1 = parsedDoc.querySelector('h1')?.innerHTML;
    const sub = parsedDoc.querySelector('h1 + p')?.innerHTML;
    return { h1, sub };
  }, [parsedDoc]);

  const uspsSection = React.useMemo(() => {
    if (!parsedDoc) return null;
    const allH2 = Array.from(parsedDoc.querySelectorAll('h2'));
    const h2 = allH2.find(el => el.textContent.toLowerCase().includes('hvorfor')) || allH2[0];
    if (!h2) return null;
    const heading = h2.innerHTML;
    
    // Parse USP items if present under this section
    const items = [];
    let curr = h2.nextElementSibling;
    let currItem = null;
    while (curr && curr.tagName !== 'H2') {
      if (curr.tagName === 'H3') {
        if (currItem) items.push(currItem);
        currItem = { title: curr.innerHTML, description: '' };
      } else if (curr.tagName === 'P' && currItem) {
        currItem.description = curr.innerHTML;
      }
      curr = curr.nextElementSibling;
    }
    if (currItem) items.push(currItem);

    return { heading, items: items.length > 0 ? items : null };
  }, [parsedDoc]);

  const aboutSection = React.useMemo(() => {
    if (!parsedDoc) return null;
    const allH2 = Array.from(parsedDoc.querySelectorAll('h2'));
    const h2 = allH2.find(el => el.textContent.toLowerCase().includes('mød') || el.textContent.toLowerCase().includes('om ecolaser'));
    if (!h2) return null;
    const heading = h2.innerHTML;
    const paragraphs = [];
    let curr = h2.nextElementSibling;
    while (curr && curr.tagName !== 'H2') {
      if (curr.tagName === 'P') {
        paragraphs.push(curr.innerHTML);
      }
      curr = curr.nextElementSibling;
    }
    return { heading, paragraphs };
  }, [parsedDoc]);

  const heritageSection = React.useMemo(() => {
    if (!parsedDoc) return null;
    const allH2 = Array.from(parsedDoc.querySelectorAll('h2'));
    const h2 = allH2.find(el => {
      const t = el.textContent.toLowerCase();
      return t.includes('fredede') || t.includes('bevaringsværdige') || (t.includes('restaurering') && t.includes('byggeri'));
    });
    if (!h2) return null;
    const heading = h2.innerHTML;
    const paragraphs = [];
    let curr = h2.nextElementSibling;
    while (curr && curr.tagName !== 'H2') {
      if (curr.tagName === 'P') {
        paragraphs.push(curr.innerHTML);
      }
      curr = curr.nextElementSibling;
    }
    return { heading, paragraphs };
  }, [parsedDoc]);

  const facadesMasonrySection = React.useMemo(() => {
    if (!parsedDoc) return null;
    const allH2 = Array.from(parsedDoc.querySelectorAll('h2'));
    const h2 = allH2.find(el => {
      const t = el.textContent.toLowerCase();
      return (t.includes('facader') || t.includes('facaderens')) && t.includes('murværk');
    });
    if (!h2) return null;
    const heading = h2.innerHTML;
    const paragraphs = [];
    let curr = h2.nextElementSibling;
    while (curr && curr.tagName !== 'H2') {
      if (curr.tagName === 'P') {
        paragraphs.push(curr.innerHTML);
      }
      curr = curr.nextElementSibling;
    }
    return { heading, paragraphs };
  }, [parsedDoc]);

  const focusSection = React.useMemo(() => {
    if (!parsedDoc) return null;
    const allH2 = Array.from(parsedDoc.querySelectorAll('h2'));
    const h2 = allH2.find(el => {
      const t = el.textContent.toLowerCase();
      return t.includes('fokus') || t.includes('øvrige');
    });
    if (!h2) return null;
    const heading = h2.innerHTML;
    const sub = h2.nextElementSibling?.tagName === 'P' ? h2.nextElementSibling.innerHTML : null;
    return { heading, sub };
  }, [parsedDoc]);

  // Sort and filter services for the other focus areas (items 3: graffiti, 4: industri, 5: rust/metal)
  const otherServices = React.useMemo(() => {
    const list = displayServices && displayServices.length > 0 ? [...displayServices] : [...fallbackServices];
    list.sort((a, b) => {
      const wA = a.weight !== undefined && a.weight !== null ? Number(a.weight) : 99;
      const wB = b.weight !== undefined && b.weight !== null ? Number(b.weight) : 99;
      return wA - wB;
    });

    const targetSlugs = ['graffiti-beton-natursten', 'industri-skadeservice', 'rust-metaloverflader'];
    const matched = targetSlugs.map((tSlug) => {
      return list.find(s => getServiceRouteSlug(s) === tSlug);
    }).filter(Boolean);

    if (matched.length === 3) return matched;
    return list.slice(2, 5);
  }, [displayServices]);

  const beforeAfterSection = React.useMemo(() => {
    if (!parsedDoc) return null;
    const allH2 = Array.from(parsedDoc.querySelectorAll('h2'));
    const h2 = allH2.find(el => el.textContent.toLowerCase().includes('før') || el.textContent.toLowerCase().includes('efter'));
    if (!h2) return null;
    const heading = h2.innerHTML;
    const sub = h2.nextElementSibling?.tagName === 'P' ? h2.nextElementSibling.innerHTML : null;
    return { heading, sub };
  }, [parsedDoc]);

  const partnersSection = React.useMemo(() => {
    if (!parsedDoc) return null;
    const allH2 = Array.from(parsedDoc.querySelectorAll('h2'));
    const h2 = allH2.find(el => el.textContent.toLowerCase().includes('professionelle') || el.textContent.toLowerCase().includes('aktører'));
    if (!h2) return null;
    const heading = h2.innerHTML;
    const paragraphs = [];
    const items = [];
    let curr = h2.nextElementSibling;
    let currItem = null;
    while (curr && curr.tagName !== 'H2') {
      if (curr.tagName === 'H3') {
        if (currItem) items.push(currItem);
        currItem = { title: curr.innerHTML, description: '' };
      } else if (curr.tagName === 'P') {
        if (currItem) {
          currItem.description = curr.innerHTML;
        } else {
          paragraphs.push(curr.innerHTML);
        }
      }
      curr = curr.nextElementSibling;
    }
    if (currItem) items.push(currItem);

    return { heading, paragraphs, items: items.length > 0 ? items : null };
  }, [parsedDoc]);

  const ctaSection = React.useMemo(() => {
    if (!parsedDoc) return null;
    const allH2 = Array.from(parsedDoc.querySelectorAll('h2'));
    const h2 = allH2.find(el => el.textContent.toLowerCase().includes('vurdering') || el.textContent.toLowerCase().includes('tilbud'));
    if (!h2) return null;
    const heading = h2.innerHTML;
    const sub = h2.nextElementSibling?.tagName === 'P' ? h2.nextElementSibling.innerHTML : null;
    return { heading, sub };
  }, [parsedDoc]);

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Laserrens i hele Danmark | Byggeri, facade og industri | Hør mere</title>
        <meta name="description" content="Få professionel laserrens til byggeri, facader, historiske ejendomme og industri i hele Danmark. Mobil laserafrensning uden procesvand og kemi." />
        <meta property="og:title" content="Laserrens i hele Danmark | Byggeri, facade og industri | Hør mere" />
        <meta property="og:description" content="Få professionel laserrens til byggeri, facader, historiske ejendomme og industri i hele Danmark. Mobil laserafrensning uden procesvand og kemi." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/cropped-picture1.jpg" />
        <html lang="da" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center pt-44 md:pt-52 pb-24 md:pb-32 overflow-hidden bg-zinc-950">
        {/* Full-width Background Image */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src={(Array.isArray(pageData?.images) && pageData.images.length > 0) ? (pageData.images[0].includes('assets.auxodev.dk') && !pageData.images[0].includes('/cdn-cgi/image/') ? pageData.images[0].replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/') : pageData.images[0]) : "https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/imgi-13-0820-1.png"}
            alt="Ecolaser Hero"
            className="w-full h-full object-cover animate-slow-zoom"
          />
          {/* Dark overlay to ensure high contrast and readability for the text */}
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-3xl mx-auto flex flex-col items-center gap-6 text-center text-white">
            {/* Environmental Badge */}
            <div className="inline-flex items-center gap-2 bg-brand-green/20 border border-brand-green/30 px-4 py-1.5 rounded-full text-brand-green text-xs font-semibold uppercase tracking-wider mb-2">
              <i className="fa-solid fa-droplet-slash text-brand-green animate-pulse" /> Skåner vores kostbare grundvand
            </div>

            {/* Main Heading */}
            {heroContent?.h1 ? (
              <h1 
                className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-wide text-white [&_span]:text-brand-green"
                dangerouslySetInnerHTML={{ __html: heroContent.h1 }}
              />
            ) : (
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-wide text-white">
                Specialist i laserrens af <span className="text-brand-green">historiske ejendomme</span> & moderne byggeri
              </h1>
            )}

            {/* Subheading */}
            {heroContent?.sub ? (
              <p 
                className="text-zinc-200 text-sm md:text-base max-w-2xl leading-relaxed"
                dangerouslySetInnerHTML={{ __html: heroContent.sub }}
              />
            ) : (
              <p className="text-zinc-200 text-sm md:text-base max-w-2xl leading-relaxed">
                Vi fjerner belægningen – men bevarer historien. Oplev en 100% kemifri, tør og skånsom overfladerens med kirurgisk præcision – helt uden at bruge af vores kostbare grundvand.
              </p>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-2 w-full sm:w-auto">
              <Link
                to="/kontakt"
                className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-light text-white font-bold px-8 py-3.5 rounded-lg text-xs uppercase tracking-wider transition-all duration-300 hover:shadow-[0_0_20px_rgba(118,184,42,0.4)] flex items-center justify-center"
              >
                Få et tilbud
              </Link>
              <Link
                to="/om-os"
                className="w-full sm:w-auto bg-white hover:bg-zinc-100 text-zinc-950 font-bold px-8 py-3.5 rounded-lg text-xs uppercase tracking-wider transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg"
              >
                Læs om os
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Hvorfor vælge Ecolaser (USPs) */}
      <section className="bg-white py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center flex flex-col items-center gap-4 mb-16">
            <h2 
              className="text-2xl md:text-3xl font-bold text-zinc-900"
              dangerouslySetInnerHTML={{ __html: uspsSection?.heading || 'Hvorfor vælge Ecolaser?' }}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-y-12 gap-x-8 mt-12">
            {(uspsSection?.items && uspsSection.items.length > 0 ? uspsSection.items : usps).slice(0, showAllUsps ? (uspsSection?.items?.length || usps.length) : 3).map((usp, idx) => (
              <div
                key={usp.id || idx}
                className="flex flex-col items-center text-center gap-3"
              >
                <h3 
                  className="text-xl font-bold text-zinc-900"
                  dangerouslySetInnerHTML={{ __html: usp.title }}
                />
                <p 
                  className="text-zinc-600 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: usp.description }}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-12">
            <button
              onClick={() => setShowAllUsps(!showAllUsps)}
              className="bg-zinc-100 hover:bg-zinc-200 text-zinc-800 font-bold px-6 py-3 rounded-lg text-xs uppercase tracking-wider transition-all duration-300 flex items-center gap-2 cursor-pointer"
            >
              {showAllUsps ? (
                <>
                  Vis færre fordele <i className="fa-solid fa-chevron-up" />
                </>
              ) : (
                <>
                  Vis flere fordele <i className="fa-solid fa-chevron-down" />
                </>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* Om os Sektion med Video */}
      <section className="bg-zinc-50 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Text */}
            <div className="lg:col-span-6 space-y-6">
              <h2 
                className="text-2xl md:text-3xl font-bold text-zinc-900"
                dangerouslySetInnerHTML={{ __html: aboutSection?.heading || 'Mød ECOLASER' }}
              />
              {aboutSection?.paragraphs && aboutSection.paragraphs.length > 0 ? (
                aboutSection.paragraphs.map((pText, pIdx) => (
                  <p
                    key={pIdx}
                    className={pIdx === 0 ? "text-zinc-800 font-semibold text-base md:text-lg leading-relaxed" : "text-zinc-600 text-sm md:text-base leading-relaxed"}
                    dangerouslySetInnerHTML={{ __html: pText }}
                  />
                ))
              ) : (
                <>
                  <p className="text-zinc-800 font-semibold text-base md:text-lg leading-relaxed">
                    Hos ECOLASER er vi specialister i laserrens, som er fremtidens skånsomme, kemifrie og vandfrie metode. Da vi ikke bruger vand, er laserrens helt unik til afrensning indenfor uden risiko for fugtskader.
                  </p>
                  <p className="text-zinc-600 text-sm md:text-base leading-relaxed">
                    ECOLASER er stiftet og drives af Poul René Nielsen. Virksomheden forener solid praktisk erfaring med <strong>stærke analytiske og kommunikative kompetencer fra sin uddannelsesbaggrund</strong>. Dette sikrer en metodisk tilgang til faget, hvor komplekse projekter gribes struktureret an, og hvor den professionelle dialog med kunden altid er i højsædet.
                  </p>
                </>
              )}
              <div className="pt-2 flex flex-wrap items-center gap-6">
                <Link
                  to="/om-os"
                  className="inline-flex items-center gap-2 text-zinc-900 text-sm font-bold uppercase tracking-wider hover:text-zinc-700 transition-colors"
                >
                  Læs mere om os <i className="fa-solid fa-arrow-right" />
                </Link>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center bg-brand-green hover:bg-brand-green-light text-white font-bold px-6 py-3 rounded-lg text-xs uppercase tracking-wider transition-all duration-300 hover:shadow-[0_4px_12px_rgba(118,184,42,0.3)]"
                >
                  Kontakt os <i className="fa-solid fa-arrow-right ml-2" />
                </Link>
              </div>
            </div>

            {/* Right Column: Video */}
            <div className="lg:col-span-6">
              <div className="aspect-video rounded-2xl overflow-hidden shadow-xl bg-zinc-900 relative">
                <video
                  src="/images/Video/video-til-hjemmeside-om-ydelser.mp4"
                  poster="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/2000028847-264cb2bf721f179d44fe7574473e878bce9c216c36f73e495fb6a65ef2c1ff8f-d.jpg"
                  controls
                  muted
                  loop
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Speciale 1: Laserrensning af fredede og bevaringsværdige bygninger (Nr. 1) */}
      <section className="bg-white py-20 md:py-24 border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Text */}
            <div className="lg:col-span-7 space-y-6">
              <h2 
                className="text-2xl md:text-3xl lg:text-4xl font-bold text-zinc-900 leading-tight"
                dangerouslySetInnerHTML={{ __html: heritageSection?.heading || 'Laserrensning af fredede og bevaringsværdige bygninger' }}
              />
              <div className="space-y-4 text-zinc-600 leading-relaxed text-sm md:text-base">
                {heritageSection?.paragraphs && heritageSection.paragraphs.length > 0 ? (
                  heritageSection.paragraphs.map((p, i) => (
                    <p key={i} className="[&_a]:text-zinc-900 [&_a]:underline [&_a]:decoration-brand-green [&_a]:decoration-2 [&_a]:underline-offset-4 [&_a]:font-medium hover:[&_a]:text-brand-green transition-colors" dangerouslySetInnerHTML={{ __html: p }} />
                  ))
                ) : (
                  <>
                    <p>
                      Fredede og bevaringsværdige bygninger er vores absolutte kernespeciale. Afrensning af historisk kulturarv stiller de højeste krav til faglig ekspertise, materialeforståelse og præcision, da en forkert metode kan forvolde uoprettelige skader på sarte overflader.
                    </p>
                    <p>
                      Med mobil laserrens fjerner vi sod, snavs, trafikfilm og graffiti med mikroskopisk nøjagtighed – 100% kemifrit, tørt og uden mekanisk slid. Metoden bevarer bygningens originale overfladestruktur og historiske patina fuldstændig intakt.
                    </p>
                    <p>
                      Vi tilpasser laserens parametre til sarte natursten som sandsten, kalksten og marmor samt historiske friser, ornamenter og stuk. Ecolaser har erfaring fra markante projekter som den <Link to="/cases/laserrensning-schweizerstil-1874-kgs-lyngby" className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium">bevaringsværdige schweizerstil i Kgs. Lyngby</Link>, de <Link to="/cases/kobenhavns-hovedbanegard-sandstensfigurer" className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium">fredede sandstensfigurer på Københavns Hovedbanegård</Link>, <Link to="/cases/komplet-afrensning-af-historisk-stationsbygning-i-soroe-fra-1856" className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium">Sorø Stationsbygning</Link> og <Link to="/cases/laserrensning-fredet-bindingsvaerk-rudkobing" className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium">Bondos Gård</Link>.
                    </p>
                  </>
                )}
              </div>
              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Link
                  to="/ydelser/fredede-bygninger"
                  className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-green-light text-white font-bold px-6 py-3 rounded-lg text-xs uppercase tracking-wider transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  Læs mere om fredede bygninger <i className="fa-solid fa-arrow-right ml-1" />
                </Link>
                <a
                  href="tel:29100810"
                  className="inline-flex items-center gap-2.5 bg-zinc-900 hover:bg-zinc-800 text-white font-bold px-6 py-3 rounded-lg text-xs uppercase tracking-wider transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  <i className="fa-solid fa-phone" /> Ring 29 10 08 10
                </a>
              </div>
            </div>

            {/* Right Column: Visual */}
            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-zinc-100 aspect-4/3 bg-zinc-900 group">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/til-hjemmeside-hele-huset-kgs-lyngby.jpg"
                  alt="Laser rens af bevaringsværdi 3 international schweizerstil fra 1874 KGS. Lyngby"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-xs p-4 rounded-xl shadow-lg border border-white/50 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-landmark text-base" />
                  </div>
                  <div className="text-xs">
                    <div className="font-bold text-zinc-900">Bevaringsværdi 3 i schweizerstil fra 1874</div>
                    <div className="text-zinc-500">Laser rens af bevaringsværdig privatbolig i Kgs. Lyngby</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Speciale 2: Facaderens med laser til murværk og byggeri (Nr. 2) */}
      <section className="bg-zinc-50 py-20 md:py-24 border-b border-zinc-200/60">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Visual (order-2 lg:order-1) */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-zinc-100 aspect-4/3 bg-zinc-900 group">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/img-3435.jpg"
                  alt="Facaderens med laser til murværk og byggeri"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-xs p-4 rounded-xl shadow-lg border border-white/50 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-trowel-bricks text-base" />
                  </div>
                  <div className="text-xs">
                    <div className="font-bold text-zinc-900">100% vandfri & kemifri proces</div>
                    <div className="text-zinc-500">Bevarer murstenens brandhud og sarte mørtelfuger</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Text (order-1 lg:order-2) */}
            <div className="lg:col-span-7 order-1 lg:order-2 space-y-6">
              <h2 
                className="text-2xl md:text-3xl lg:text-4xl font-bold text-zinc-900 leading-tight"
                dangerouslySetInnerHTML={{ __html: facadesMasonrySection?.heading || 'Facaderens med laser til murværk og byggeri' }}
              />
              <div className="space-y-4 text-zinc-600 leading-relaxed text-sm md:text-base">
                {facadesMasonrySection?.paragraphs && facadesMasonrySection.paragraphs.length > 0 ? (
                  facadesMasonrySection.paragraphs.map((p, i) => (
                    <p key={i} className="[&_a]:text-zinc-900 [&_a]:underline [&_a]:decoration-brand-green [&_a]:decoration-2 [&_a]:underline-offset-4 [&_a]:font-medium hover:[&_a]:text-brand-green transition-colors" dangerouslySetInnerHTML={{ __html: p }} />
                  ))
                ) : (
                  <>
                    <p>
                      Facaderens af murværk og tegl er vores andet store specialeområde. Vi hjælper murermestre, entreprenører, bygherrer og ejendomsselskaber med skånsom og effektiv afrensning på både nybyggeri og eksisterende bygninger i hele landet.
                    </p>
                    <p>
                      Ved nybyggeri og facaderenovering opstår der ofte kalkslør, fugeslør og genstridige salpeterudtræk. Hvor traditionel afsyring og højtryksspuling risikerer at ødelægge murstenens overflade eller mætte murværket med procesvand, renser laseren helt tørt og uden kemikalier. Derved elimineres risikoen for frostsprængninger og fornyet saltvandring i facaden.
                    </p>
                    <p>
                      Laserstrålen fordamper udelukkende den uønskede belægning og efterlader murstenens vitale brandhud og de originale mørtelfuger fuldstændig ubeskadigede.
                    </p>
                  </>
                )}
              </div>
              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Link
                  to="/ydelser/facaderens-murvaerk"
                  className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-green-light text-white font-bold px-6 py-3 rounded-lg text-xs uppercase tracking-wider transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  Læs mere om facaderens og murværk <i className="fa-solid fa-arrow-right ml-1" />
                </Link>
                <a
                  href="tel:29100810"
                  className="inline-flex items-center gap-2.5 bg-zinc-900 hover:bg-zinc-800 text-white font-bold px-6 py-3 rounded-lg text-xs uppercase tracking-wider transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  <i className="fa-solid fa-phone" /> Ring 29 10 08 10
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Øvrige fokusområder (Ydelser 3, 4, 5) */}
      <section className="bg-white py-24 border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center flex flex-col items-center gap-4 mb-16">
            <div className="inline-flex items-center gap-2 bg-zinc-100 border border-zinc-200 px-3.5 py-1.5 rounded-full text-zinc-700 text-xs font-semibold uppercase tracking-wider">
              Flere anvendelsesområder
            </div>
            <h2 
              className="text-2xl md:text-3xl font-bold text-zinc-900"
              dangerouslySetInnerHTML={{ __html: focusSection?.heading || 'Øvrige fokusområder' }}
            />
            <p 
              className="text-zinc-600 text-sm max-w-2xl mt-2"
              dangerouslySetInnerHTML={{ __html: focusSection?.sub || 'Ud over vores to primære specialer tilbyder vi højteknologisk, kemifri og vandfri laserrensning til en række andre materialer og brancher:' }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {otherServices.map((service, idx) => {
              const sSlug = getServiceRouteSlug(service, idx);
              const sTitle = typeof service.title === 'object' ? service.title?.da : service.title;
              const sDesc = typeof service.short_desc === 'object' ? service.short_desc?.da : service.short_desc;
              const sIcon = getServiceIcon(sTitle || sSlug, idx);
              return (
                <Link
                  key={sSlug}
                  to={`/ydelser/${sSlug}`}
                  className="group bg-zinc-50 hover:bg-white border border-zinc-200/80 hover:border-brand-green/40 rounded-2xl p-8 flex flex-col items-center text-center gap-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl relative overflow-hidden"
                >
                  <div className="w-16 h-16 rounded-xl bg-brand-green border border-brand-green flex items-center justify-center text-white text-3xl transition-transform duration-300 group-hover:scale-110 shadow-md">
                    <i className={sIcon} />
                  </div>
                  <div className="flex flex-col gap-3 flex-1 justify-between items-center w-full">
                    <div>
                      <h3 className="text-lg font-bold text-zinc-900 group-hover:text-brand-green transition-colors mb-2">
                        {sTitle}
                      </h3>
                      <p className="text-zinc-600 text-xs md:text-sm leading-relaxed line-clamp-2">
                        {sDesc}
                      </p>
                    </div>
                    <span className="text-zinc-900 text-xs font-bold uppercase tracking-wider flex items-center gap-2 mt-2 group-hover:text-brand-green transition-colors">
                      Læs mere <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Før / Efter Sektion */}
      <section className="bg-zinc-50 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center flex flex-col items-center gap-4 mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900">
              Interaktiv Før / Efter
            </h2>
            <p className="text-zinc-600 text-sm max-w-xl mt-2">
              Træk i slideren for at sammenligne overfladen før og efter vores skånsomme laserrensning.
            </p>
          </div>

          <BeforeAfterSlider limit={6} columns={3} />
        </div>
      </section>

      {/* Cases Sektion */}
      {displayCases.length > 0 && (
        <section className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center flex flex-col items-center gap-4 mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-zinc-900">Udvalgte referencecases</h2>
              <p className="text-zinc-600 text-sm max-w-xl mt-2">
                Se billeder og læs om vores seneste specialopgaver over hele landet.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {displayCases.map((c) => {
                const cTitle = typeof c.title === 'object' ? c.title?.da : c.title;
                const cImg = Array.isArray(c.image) && c.image.length > 0 ? c.image[0] : '';
                const cTarget = c.id;
                return (
                  <div
                    key={c.id}
                    className="bg-zinc-50 rounded-2xl overflow-hidden flex flex-col justify-between transition-all duration-300 hover:shadow-md"
                  >
                    <div>
                      <div className="aspect-video w-full overflow-hidden relative">
                        {cImg && (
                          <img
                            src={cImg.includes('assets.auxodev.dk') && !cImg.includes('/cdn-cgi/image/')
                              ? cImg.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/')
                              : cImg}
                            alt={cTitle}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="p-6 pb-0">
                        <h3 className="text-lg font-bold text-zinc-900 line-clamp-2">
                          {cTitle}
                        </h3>
                      </div>
                    </div>
                    <div className="p-6 pt-0 mt-4">
                      <Link
                        to={`/cases/${cTarget}`}
                        className="inline-block w-full text-center bg-brand-green hover:bg-brand-green/90 text-white font-semibold py-2.5 rounded-lg text-xs uppercase tracking-wider transition-colors"
                      >
                        Læs hele casen
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/cases"
                className="inline-flex items-center gap-2 text-zinc-900 text-sm font-bold uppercase tracking-wider hover:text-zinc-700 transition-colors"
              >
                Se alle cases <i className="fa-solid fa-arrow-right" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Partners / Professional References */}
      <section className="bg-zinc-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 
              className="text-2xl md:text-3xl font-extrabold text-zinc-900 mb-4"
              dangerouslySetInnerHTML={{ __html: partnersSection?.heading || 'Vi løser opgaver for professionelle aktører i hele landet' }}
            />
            <p 
              className="text-zinc-600 text-base md:text-lg leading-relaxed"
              dangerouslySetInnerHTML={{ __html: partnersSection?.paragraphs?.[0] || 'Hos ECOLASER er vi stolte af at være den foretrukne samarbejdspartner for en bred vifte af landets største og mest kvalitetsbevidste virksomheder. Vores laserteknologi løser opgaver på tværs af sektorer, hvor præcision og skånsomhed er i højsædet.' }}
            />
          </div>

          <div className="max-w-4xl mx-auto">
            <p 
              className="text-zinc-700 text-base md:text-lg text-center leading-relaxed mb-12 font-medium"
              dangerouslySetInnerHTML={{ __html: partnersSection?.paragraphs?.[1] || 'Uanset om opgaven kræver dyb respekt for vores fælles kulturarv eller højteknologisk renhed i moderne produktioner, leverer vi et sublimt resultat i tæt teamwork med projektets parter.' }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {partnersSection?.items && partnersSection.items.length > 0 ? (
                partnersSection.items.map((item, iIdx) => {
                  const partnerIcons = ['fa-solid fa-train', 'fa-solid fa-building', 'fa-solid fa-industry', 'fa-solid fa-city'];
                  return (
                    <div key={iIdx} className="bg-white p-6 rounded-xl border border-zinc-100 shadow-xs flex gap-4">
                      <div className="w-12 h-12 rounded-lg bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0">
                        <i className={`${partnerIcons[iIdx % partnerIcons.length]} text-lg`} />
                      </div>
                      <div>
                        <h4 
                          className="font-bold text-zinc-900 mb-2"
                          dangerouslySetInnerHTML={{ __html: item.title }}
                        />
                        <p 
                          className="text-zinc-600 text-sm leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <>
                  <div className="bg-white p-6 rounded-xl border border-zinc-100 shadow-xs flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-train text-lg" />
                    </div>
                    <div>
                      <h4 className="font-bold text-zinc-900 mb-2">Infrastruktur og jernbanenet</h4>
                      <p className="text-zinc-600 text-sm leading-relaxed">
                        Præcisionsafrensning af historiske elementer og trafikeret byggeri (f.eks. banegårde og stationer).
                      </p>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl border border-zinc-100 shadow-xs flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-building text-lg" />
                    </div>
                    <div>
                      <h4 className="font-bold text-zinc-900 mb-2">Landsdækkende entreprenør- og byggevirksomheder</h4>
                      <p className="text-zinc-600 text-sm leading-relaxed">
                        Facaderens og klargøring af overflader til både nybyggeri og renoveringsprojekter.
                      </p>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl border border-zinc-100 shadow-xs flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-industry text-lg" />
                    </div>
                    <div>
                      <h4 className="font-bold text-zinc-900 mb-2">Fødevareindustrien og produktionsanlæg</h4>
                      <p className="text-zinc-600 text-sm leading-relaxed">
                        Kemifri og skånsom afrensning af produktionsfaciliteter, hvor hygiejnekravene er tårnhøje.
                      </p>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl border border-zinc-100 shadow-xs flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-city text-lg" />
                    </div>
                    <div>
                      <h4 className="font-bold text-zinc-900 mb-2">Ejendomsselskaber og ejendomsadministrationer</h4>
                      <p className="text-zinc-600 text-sm leading-relaxed">
                        Vedligeholdelse og værdibevarelse af større ejendomsporteføljer og historiske ejendomme.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Danmarkskort Sektion */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6">
          <DenmarkMap />
        </div>
      </section>

      {/* Kundeanmeldelser (Infinite Slider) */}
      <section className="bg-zinc-50 py-24">
        <div className="text-center flex flex-col items-center gap-4 mb-12">
          <h2 className="text-xl md:text-3xl font-bold text-zinc-900">Hvad siger vores kunder?</h2>
        </div>

        <ReviewsSlider />
      </section>

      {/* CTA-sektion */}
      <section 
        className="relative py-28 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('${(Array.isArray(pageData?.images) && pageData.images.length > 1) ? (pageData.images[1].includes('assets.auxodev.dk') && !pageData.images[1].includes('/cdn-cgi/image/') ? pageData.images[1].replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/') : pageData.images[1]) : "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/imgi-94-billede-med-blur-scaled.jpg"}')`
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-zinc-950/85" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center flex flex-col items-center gap-6">
          <h2 
            className="text-3xl md:text-4xl font-extrabold text-white leading-tight"
            dangerouslySetInnerHTML={{ __html: ctaSection?.heading || 'Få en professionel vurdering af din opgave' }}
          />
          <p 
            className="text-zinc-300 text-sm md:text-base max-w-xl leading-relaxed"
            dangerouslySetInnerHTML={{ __html: ctaSection?.sub || 'Uanset om du står med en fredet facade, en rusten maskindel eller kalkudtræk på nybyggeri, står vi klar til at rådgive dig om den mest skånsomme og effektive løsning.' }}
          />
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-4 w-full sm:w-auto">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-light text-white font-bold px-8 py-4 rounded-lg text-sm uppercase tracking-wider transition-all duration-300 hover:shadow-[0_4px_12px_rgba(118,184,42,0.3)] flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-envelope" /> Kontakt os nu
            </Link>
            <Link
              to="/priser"
              className="w-full sm:w-auto bg-transparent border border-white hover:bg-white hover:text-zinc-950 text-white font-bold px-8 py-4 rounded-lg text-sm uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-calculator" /> Se priser & beregner
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
